/**
 * Lo que viaja a la IA con el prompt, en la barra del pie de la caja: la
 * industria, los objetivos elegidos y un «+» para añadir otro.
 *
 * Las respuestas de la bienvenida se contestan una vez, pero valen para el
 * sitio que se está creando, no para todos: quien hace un segundo sitio de otra
 * industria, o sin blog, las cambia aquí. Vienen rellenas con lo último que usó
 * (`welcomeAnswers`).
 *
 * Todo en una sola fila y con nombres cortos («Reservas», «Tienda»): eran tres
 * renglones de controles y se comían media caja. La industria y el «+» llevan
 * encima un `<select>` nativo transparente —en el teléfono abre la rueda del
 * sistema—; cada objetivo, su icono y una «×».
 */
import { IconChevronDown, IconPlus, IconX } from '@tabler/icons-react'
import { useI18n } from '../lib/i18n'
import {
  WELCOME_BUSINESSES,
  WELCOME_BUSINESS_LABELS,
  WELCOME_DETAIL_MAX,
  WELCOME_GOALS,
  type WelcomeAnswers,
  type WelcomeBusiness,
  type WelcomeGoal,
} from '../lib/welcomeAnswers'
import { GOAL_ICONS } from './goalIcons'

export function BriefChips({ answers, onChange }: {
  answers: WelcomeAnswers | null
  onChange: (answers: WelcomeAnswers) => void
}) {
  const { t } = useI18n()
  const business = answers?.business ?? null
  const goals = answers?.goals ?? []
  const missing = WELCOME_GOALS.filter((goal) => !goals.includes(goal))

  const set = (patch: { business?: WelcomeBusiness | null; businessDetail?: string | undefined; goals?: WelcomeGoal[] }) => {
    const next = { business, goals, businessDetail: answers?.businessDetail, ...patch }
    const detail = next.business === 'other' ? next.businessDetail : undefined
    onChange({ business: next.business, goals: next.goals, ...(detail !== undefined ? { businessDetail: detail } : {}) })
  }

  return (
    <div className="cwa-brief" role="group" aria-label={t('brief.aria')}>
      <label className={`cwa-brief-btn${business ? '' : ' is-empty'}`} title={t('brief.business')}>
        <span className="cwa-brief-btn-value">{business ? t(WELCOME_BUSINESS_LABELS[business]) : t('brief.business')}</span>
        <IconChevronDown size={12} aria-hidden="true" />
        <select
          value={business ?? ''}
          aria-label={t('brief.business')}
          onChange={(event) => set({ business: (event.target.value || null) as WelcomeBusiness | null })}
        >
          <option value="">{t('brief.none')}</option>
          {WELCOME_BUSINESSES.map((key) => <option key={key} value={key}>{t(WELCOME_BUSINESS_LABELS[key])}</option>)}
        </select>
      </label>
      {business === 'other' ? (
        <input
          className="cwa-brief-detail"
          type="text"
          value={answers?.businessDetail ?? ''}
          maxLength={WELCOME_DETAIL_MAX}
          aria-label={t('welcome.business.otherLabel')}
          placeholder={t('welcome.business.otherPlaceholder')}
          onChange={(event) => set({ businessDetail: event.target.value })}
        />
      ) : null}
      {goals.length ? <span className="cwa-brief-sep" aria-hidden="true" /> : null}
      {goals.map((goal) => {
        const Icon = GOAL_ICONS[goal]
        return (
          <span key={goal} className="cwa-goal-banner" data-goal={goal} title={t(`welcome.goal.${goal}`)}>
            <span className="cwa-goal-banner-icon" aria-hidden="true"><Icon size={12} stroke={2} /></span>
            <span className="cwa-goal-banner-label">{t(`brief.goalShort.${goal}`)}</span>
            <button type="button" className="cwa-goal-banner-close" aria-label={t('brief.removeGoal', { name: t(`welcome.goal.${goal}`) })} onClick={() => set({ goals: goals.filter((item) => item !== goal) })}>
              <IconX size={11} aria-hidden="true" />
            </button>
          </span>
        )
      })}
      {missing.length ? (
        <label className="cwa-brief-btn is-add">
          <IconPlus size={13} aria-hidden="true" />
          <span className="cwa-brief-btn-value">{t('brief.addGoal')}</span>
          <select
            value=""
            aria-label={t('brief.addGoal')}
            onChange={(event) => { if (event.target.value) set({ goals: [...goals, event.target.value as WelcomeGoal] }) }}
          >
            <option value="" disabled>{t('brief.addGoal')}</option>
            {missing.map((goal) => <option key={goal} value={goal}>{t(`welcome.goal.${goal}`)}</option>)}
          </select>
        </label>
      ) : null}
    </div>
  )
}
